"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.JEVMImportTransaction = exports.JEVMExportTransaction = exports.EVMInput = exports.EVMOutput = void 0;
const utils_1 = require("../../utils");
const constants_1 = require("../constants");
const input_1 = require("../input");
const output_1 = require("../output");
const signature_1 = require("../signature");
const transaction_1 = require("../transaction");
const types_1 = require("../types");
class EVMOutput {
    address;
    amount;
    assetId;
    constructor(address, amount, assetId) {
        this.address = address;
        this.amount = amount;
        this.assetId = assetId;
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(constants_1.EVMOutputSize);
        buffer.write(this.address);
        buffer.writeUInt64(this.amount);
        buffer.write(this.assetId);
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        const address = new types_1.Address(reader.read(constants_1.AddressSize));
        const amount = reader.readUInt64();
        const assetId = new types_1.AssetId(reader.read(constants_1.AssetIdSize).toCB58());
        return new EVMOutput(address, amount, assetId);
    }
    static comparator = (a, b) => {
        const comparison = types_1.Address.comparator(a.address, b.address);
        if (comparison !== 0) {
            return comparison;
        }
        return types_1.AssetId.comparator(a.assetId, b.assetId);
    };
}
exports.EVMOutput = EVMOutput;
class EVMInput extends signature_1.AbstractSignable {
    address;
    amount;
    assetId;
    nonce;
    constructor(address, amount, assetId, nonce) {
        super();
        this.address = address;
        this.amount = amount;
        this.assetId = assetId;
        this.nonce = nonce;
    }
    getTypeId() {
        return constants_1.InvalidTypeId;
    }
    getAmount() {
        return this.amount;
    }
    getAssetId() {
        return this.assetId;
    }
    getAddresses() {
        return [this.address];
    }
    getThreshold() {
        return 1;
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(constants_1.EVMInputSize);
        buffer.write(this.address);
        buffer.writeUInt64(this.amount);
        buffer.write(this.assetId);
        buffer.writeUInt64(this.nonce);
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        const address = new types_1.Address(reader.read(constants_1.AddressSize));
        const amount = reader.readUInt64();
        const assetId = new types_1.AssetId(reader.read(constants_1.AssetIdSize).toCB58());
        const nonce = reader.readUInt64();
        return new EVMInput(address, amount, assetId, nonce);
    }
    static comparator = (a, b) => {
        let comparison = utils_1.JuneoBuffer.comparator(a.address.serialize(), b.address.serialize());
        if (comparison === 0) {
            comparison = utils_1.JuneoBuffer.comparator(a.assetId.serialize(), b.assetId.serialize());
        }
        return comparison;
    };
}
exports.EVMInput = EVMInput;
class JEVMExportTransaction extends transaction_1.ExportTransaction {
    evmInputs;
    constructor(networkId, blockchainId, destinationChain, inputs, exportedOutputs) {
        super(constants_1.EVMExportTransactionTypeId, networkId, blockchainId, [], [], '', destinationChain, exportedOutputs);
        this.evmInputs = inputs;
        this.evmInputs.sort(EVMInput.comparator);
    }
    getSignables() {
        return this.evmInputs;
    }
    serialize() {
        const inputsBytes = [];
        let inputsSize = 0;
        for (const input of this.evmInputs) {
            const bytes = input.serialize();
            inputsSize += bytes.length;
            inputsBytes.push(bytes);
        }
        const outputsBytes = [];
        let outputsSize = 0;
        for (const output of this.exportedOutputs) {
            const bytes = output.serialize();
            outputsSize += bytes.length;
            outputsBytes.push(bytes);
        }
        const buffer = utils_1.JuneoBuffer.alloc(
        // 2 + 4 + 4 + 4 + 4 = 18
        18 + constants_1.BlockchainIdSize * 2 + inputsSize + outputsSize);
        buffer.writeUInt16(this.codecId);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt32(this.networkId);
        buffer.write(this.blockchainId);
        buffer.write(this.destinationChain);
        buffer.writeUInt32(this.evmInputs.length);
        for (const input of inputsBytes) {
            buffer.write(input);
        }
        buffer.writeUInt32(this.exportedOutputs.length);
        for (const output of outputsBytes) {
            buffer.write(output);
        }
        return buffer;
    }
    static parse(data) {
        const buffer = utils_1.JuneoBuffer.from(data);
        const reader = buffer.createReader();
        // skip codec reading
        reader.skip(2);
        reader.readAndVerifyTypeId(constants_1.EVMExportTransactionTypeId);
        const networkId = reader.readUInt32();
        const blockchainId = new types_1.BlockchainId(reader.read(constants_1.BlockchainIdSize).toCB58());
        const destinationChain = new types_1.BlockchainId(reader.read(constants_1.BlockchainIdSize).toCB58());
        const inputsLength = reader.readUInt32();
        const inputs = [];
        for (let i = 0; i < inputsLength; i++) {
            const input = EVMInput.parse(reader.peekRemaining());
            reader.skip(input);
            inputs.push(input);
        }
        const exportedOutputsLength = reader.readUInt32();
        const exportedOutputs = [];
        for (let i = 0; i < exportedOutputsLength; i++) {
            const output = output_1.TransferableOutput.parse(reader.peekRemaining());
            reader.skip(output);
            exportedOutputs.push(output);
        }
        return new JEVMExportTransaction(networkId, blockchainId, destinationChain, inputs, exportedOutputs);
    }
    static estimateSignaturesCount(exportedAssets, exportFeeAssetId, importFeeAssetId) {
        // see if import/export fee outputs will be merged with exported assets
        const ignoreImportFee = exportedAssets.includes(importFeeAssetId);
        const ignoreExportFee = exportedAssets.includes(exportFeeAssetId);
        let feeInputsCount = ignoreImportFee ? 0 : 1;
        // import and export fee could also be merged together
        if (!ignoreExportFee && exportFeeAssetId !== importFeeAssetId) {
            feeInputsCount += 1;
        }
        return exportedAssets.length + feeInputsCount;
    }
    static estimateSize(inputsCount) {
        // for now consider inputs + 2 outputs for fees outputs
        const outputsCount = inputsCount;
        // 2 + 4 + 4 + 4 + 4 = 18
        return 18 + constants_1.BlockchainIdSize * 2 + constants_1.EVMInputSize * inputsCount + this.estimateOutputsSize(outputsCount);
    }
    static estimateOutputsSize(outputsCount) {
        // TransferableOutput size
        // 4 + 8 + 8 + 4 + 4 = 28 + 20 * addressesCount
        // addresses count most likely will be 1 so = 48
        return constants_1.AssetIdSize + 48 * outputsCount;
    }
}
exports.JEVMExportTransaction = JEVMExportTransaction;
class JEVMImportTransaction extends transaction_1.ImportTransaction {
    evmOutputs;
    constructor(networkId, blockchainId, sourceChain, importedInputs, evmOutputs) {
        super(constants_1.EVMImportTransactionTypeId, networkId, blockchainId, [], [], '', sourceChain, importedInputs);
        this.importedInputs.sort(input_1.TransferableInput.comparator);
        this.evmOutputs = evmOutputs;
        this.evmOutputs.sort(EVMOutput.comparator);
    }
    serialize() {
        const inputsBytes = [];
        let inputsSize = 0;
        for (const input of this.importedInputs) {
            const bytes = input.serialize();
            inputsSize += bytes.length;
            inputsBytes.push(bytes);
        }
        const outputsBytes = [];
        let outputsSize = 0;
        for (const output of this.evmOutputs) {
            const bytes = output.serialize();
            outputsSize += bytes.length;
            outputsBytes.push(bytes);
        }
        const buffer = utils_1.JuneoBuffer.alloc(
        // 2 + 4 + 4 + 4 + 4 = 18
        18 + constants_1.BlockchainIdSize * 2 + inputsSize + outputsSize);
        buffer.writeUInt16(this.codecId);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt32(this.networkId);
        buffer.write(this.blockchainId);
        buffer.write(this.sourceChain);
        buffer.writeUInt32(this.importedInputs.length);
        for (const input of inputsBytes) {
            buffer.write(input);
        }
        buffer.writeUInt32(this.evmOutputs.length);
        for (const output of outputsBytes) {
            buffer.write(output);
        }
        return buffer;
    }
    static parse(data) {
        const buffer = utils_1.JuneoBuffer.from(data);
        const reader = buffer.createReader();
        // skip codec reading
        reader.skip(2);
        reader.readAndVerifyTypeId(constants_1.EVMImportTransactionTypeId);
        const networkId = reader.readUInt32();
        const blockchainId = new types_1.BlockchainId(reader.read(constants_1.BlockchainIdSize).toCB58());
        const sourceChain = new types_1.BlockchainId(reader.read(constants_1.BlockchainIdSize).toCB58());
        const importedInputsLength = reader.readUInt32();
        const importedInputs = [];
        for (let i = 0; i < importedInputsLength; i++) {
            const input = input_1.TransferableInput.parse(reader.peekRemaining());
            reader.skip(input);
            importedInputs.push(input);
        }
        const outputsLength = reader.readUInt32();
        const outputs = [];
        for (let i = 0; i < outputsLength; i++) {
            const output = EVMOutput.parse(reader.peekRemaining());
            reader.skip(output);
            outputs.push(output);
        }
        return new JEVMImportTransaction(networkId, blockchainId, sourceChain, importedInputs, outputs);
    }
    static estimateSize(inputsCount, outputsCount) {
        // 2 + 4 + 4 + 4 + 4 = 18
        return 18 + constants_1.BlockchainIdSize * 2 + this.estimateInputsSize(inputsCount) + constants_1.EVMOutputSize * outputsCount;
    }
    static estimateInputsSize(inputsCount) {
        // TransferableInput size
        // 4 + 8 + 4 = 16 + 4 * addressesCount
        // addresses count most likely will be 1 so = 20
        return (constants_1.TransactionIdSize + 4 + constants_1.AssetIdSize + 20) * inputsCount;
    }
}
exports.JEVMImportTransaction = JEVMImportTransaction;
